import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Users, Calendar, Utensils, BookOpen, CheckCircle, XCircle, Clock, Search, Shield, Ban, UserCheck, Eye, Mail, Phone, Linkedin, Instagram, ChevronDown, ChevronUp, Filter, Plus, Edit2, Trash2, X, Save, Image, Upload, Camera, Settings, ToggleLeft, ToggleRight, Bell, Send } from 'lucide-react';

// Event categories
const EVENT_CATEGORIES = ['Networking', 'Workshop', 'Speaker Series', 'Social', 'Professional Development', 'Annual Event', 'Special Event'];
const DINNER_CATEGORIES = ['Networking', 'Fine Dining', 'Wine Tasting'];

// Get backend URL for API calls
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const { isAdmin, loading, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [dinnerEvents, setDinnerEvents] = useState([]);
  const [bookClubData, setBookClubData] = useState({ books: [], venues: [] });
  const [dataLoading, setDataLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [expandedApp, setExpandedApp] = useState(null);
  
  // Modal states for editing
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDinnerModal, setShowDinnerModal] = useState(false);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showVenueModal, setShowVenueModal] = useState(false);
  
  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
  const [editingItem, setEditingItem] = useState(null);
  const [memberApplication, setMemberApplication] = useState(null);
  
  // Form states - separate for each modal type
  const [eventForm, setEventForm] = useState({});
  const [dinnerForm, setDinnerForm] = useState({});
  const [bookForm, setBookForm] = useState({});
  const [venueForm, setVenueForm] = useState({});
  
  // Image upload states
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Settings state
  const [membersVisible, setMembersVisible] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Custom email state
  const [customEmailSubject, setCustomEmailSubject] = useState('');
  const [customEmailMessage, setCustomEmailMessage] = useState('');
  const [customEmailLoading, setCustomEmailLoading] = useState(false);
  const [customEmailResult, setCustomEmailResult] = useState(null);

  // Image upload function
  const uploadImage = async (file, folder) => {
    if (!file || !user) return null;
    
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}-${Date.now()}.${fileExt}`;
    const filePath = `${folder}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('member-photos')
      .upload(filePath, file);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      throw uploadError;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('member-photos')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  // Handle image upload for events
  const handleEventImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const url = await uploadImage(file, 'events');
      if (url) setEventForm({ ...eventForm, image_url: url });
    } catch (error) {
      alert('Failed to upload image. Please try again.');
    }
    setUploadingImage(false);
  };

  // Handle image upload for dinner events
  const handleDinnerImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const url = await uploadImage(file, 'dinner-events');
      if (url) setDinnerForm({ ...dinnerForm, image_url: url });
    } catch (error) {
      alert('Failed to upload image. Please try again.');
    }
    setUploadingImage(false);
  };

  // Handle image upload for books
  const handleBookImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(true);
    try {
      const url = await uploadImage(file, 'books');
      if (url) setBookForm({ ...bookForm, photo_url: url });
    } catch (error) {
      alert('Failed to upload image. Please try again.');
    }
    setUploadingImage(false);
  };

  // Fetch functions
  const fetchApplications = useCallback(async () => {
    const { data } = await supabase.from('applications').select('*').order('applied_at', { ascending: false });
    if (data) setApplications(data);
  }, []);

  const fetchMembers = useCallback(async () => {
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    if (data) setMembers(data);
  }, []);

  const fetchEvents = useCallback(async () => {
    const { data } = await supabase.from('events').select('*').order('event_date', { ascending: true });
    if (data) setEvents(data);
  }, []);

  const fetchDinnerEvents = useCallback(async () => {
    const { data } = await supabase.from('dinner_club_events').select('*').order('event_date', { ascending: true });
    if (data) setDinnerEvents(data);
  }, []);

  const fetchBookClubData = useCallback(async () => {
    const [booksRes, venuesRes] = await Promise.all([
      supabase.from('book_club_books').select('*').order('created_at', { ascending: false }),
      supabase.from('book_club_venues').select('*').order('meeting_date', { ascending: true })
    ]);
    setBookClubData({ books: booksRes.data || [], venues: venuesRes.data || [] });
  }, []);

  // Fetch app settings
  const fetchSettings = useCallback(async () => {
    const { data } = await supabase
      .from('app_settings')
      .select('*')
      .eq('setting_key', 'members_visible')
      .maybeSingle();

    if (data && data.setting_value) {
      setMembersVisible(data.setting_value.enabled);
    }
  }, []);

  // Toggle member directory visibility
  const toggleMembersVisibility = async () => {
    setSettingsLoading(true);
    const newValue = !membersVisible;
    
    try {
      // First try to update existing setting
      const { data: existing } = await supabase
        .from('app_settings')
        .select('*')
        .eq('setting_key', 'members_visible')
        .maybeSingle();

      if (existing) {
        await supabase
          .from('app_settings')
          .update({ setting_value: { enabled: newValue } })
          .eq('setting_key', 'members_visible');
      } else {
        // Create the setting if it doesn't exist
        await supabase
          .from('app_settings')
          .insert({ setting_key: 'members_visible', setting_value: { enabled: newValue } });
      }
      
      setMembersVisible(newValue);
    } catch (error) {
      console.error('Failed to update setting:', error);
      alert('Failed to update setting');
    }
    setSettingsLoading(false);
  };

  // Send event reminder emails
  const sendEventReminders = async (reminderType = '24h') => {
    setReminderLoading(true);
    setReminderResult(null);
    
    const hoursMap = { '24h': 24, '4h': 4 };
    
    try {
      const response = await fetch(`${API_URL}/api/email/send-reminders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          portal_url: window.location.origin,
          hours_before: hoursMap[reminderType] || 24,
          reminder_type: reminderType
        })
      });
      
      const data = await response.json();
      setReminderResult({ ...data, reminderType });
      
      // Get total_sent from results object (where the backend puts it)
      const totalSent = data.results?.total_sent || 0;
      
      if (data.status === 'completed') {
        if (totalSent > 0) {
          alert(`Successfully sent ${totalSent} ${reminderType} reminder email(s)!`);
        } else {
          alert(`All members have already been notified for ${reminderType === '4h' ? '4-hour' : '24-hour'} reminders.`);
        }
      } else if (data.status === 'skipped') {
        alert('Email service not configured. Please set up RESEND_API_KEY.');
      }
    } catch (error) {
      console.error('Failed to send reminders:', error);
      alert('Failed to send reminders: ' + error.message);
      setReminderResult({ status: 'error', message: error.message });
    }
    
    setReminderLoading(false);
  };

  // Fetch scheduler status
  const fetchSchedulerStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/scheduler/status`);
      const data = await response.json();
      setSchedulerStatus(data);
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
    }
  };

  // Send custom email to members
  const sendCustomEmail = async () => {
    console.log('sendCustomEmail called', { customEmailSubject, customEmailMessage });
    
    if (!customEmailSubject.trim() || !customEmailMessage.trim()) {
      alert('Please enter both a subject and message.');
      return;
    }
    
    setCustomEmailLoading(true);
    setCustomEmailResult(null);
    
    try {
      console.log('Sending request to API...');
      const response = await fetch(`${API_URL}/api/email/send-custom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: customEmailSubject,
          message: customEmailMessage,
          send_to: 'all'
        })
      });
      
      const data = await response.json();
      console.log('API response:', data);
      setCustomEmailResult(data);
      
      if (data.status === 'completed') {
        setCustomEmailSubject('');
        setCustomEmailMessage('');
      }
    } catch (error) {
      console.error('Failed to send custom email:', error);
      setCustomEmailResult({ status: 'error', message: error.message });
    }
    
    setCustomEmailLoading(false);
  };

  useEffect(() => {
    const loadData = async () => {
      setDataLoading(true);
      await Promise.all([fetchApplications(), fetchMembers(), fetchEvents(), fetchDinnerEvents(), fetchBookClubData(), fetchSettings(), fetchSchedulerStatus()]);
      setDataLoading(false);
    };
    if (isAdmin) loadData();
  }, [isAdmin, fetchApplications, fetchMembers, fetchEvents, fetchDinnerEvents, fetchBookClubData, fetchSettings]);

  // Application handlers
  const handleApprove = async (application) => {
    setActionLoading(application.id);
    try {
      // Update application status
      const { error: updateError } = await supabase.from('applications').update({ status: 'approved', reviewed_at: new Date().toISOString() }).eq('id', application.id);
      if (updateError) {
        console.error('Failed to update application:', updateError);
        alert('Failed to update application status: ' + updateError.message);
        setActionLoading(null);
        return;
      }
      
      // Get next member number
      const { data: existingProfiles } = await supabase.from('profiles').select('member_number').order('member_number', { ascending: false }).limit(1);
      const nextMemberNumber = (existingProfiles?.[0]?.member_number || 0) + 1;
      
      // Create profile for the approved member
      const profileData = {
        user_id: application.user_id, 
        full_name: application.full_name, 
        phone_number: application.phone_number, 
        headshot_url: application.headshot_url,
        company: application.company, 
        role: application.role || application.profession, 
        work_description: application.profession,
        linkedin_url: application.linkedin_url, 
        instagram_url: application.instagram_url, 
        membership_tier: 'founder',
        membership_status: 'active', 
        member_number: nextMemberNumber, 
        initiation_fee_waived: true,
        payment_status: 'active',
      };
      
      console.log('Creating profile with data:', profileData);
      
      const { data: newProfile, error: profileError } = await supabase.from('profiles').insert(profileData).select();
      
      if (profileError) {
        console.error('Failed to create profile:', profileError);
        alert('Application approved but failed to create member profile: ' + profileError.message);
      } else {
        console.log('Profile created successfully:', newProfile);
      }
      
      // Send welcome email to the newly approved member
      try {
        const portalUrl = window.location.origin;
        await fetch(`${API_URL}/api/email/welcome`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipient_email: application.email,
            recipient_name: application.full_name,
            portal_url: portalUrl
          })
        });
      } catch (emailError) {
        console.error('Failed to send welcome email:', emailError);
      }
      
      await Promise.all([fetchApplications(), fetchMembers()]);
    } catch (error) { 
      console.error('Approval error:', error);
      alert('Failed to approve application: ' + error.message); 
    }
    setActionLoading(null);
  };

  const handleReject = async (application) => {
    setActionLoading(application.id);
    try {
      await supabase.from('applications').update({ status: 'rejected', reviewed_at: new Date().toISOString() }).eq('id', application.id);
      await fetchApplications();
    } catch (error) { alert('Failed to reject application'); }
    setActionLoading(null);
  };

  // Member handlers
  const handleSuspend = async (member) => {
    setActionLoading(member.id);
    try {
      const newStatus = member.membership_status === 'suspended' ? 'active' : 'suspended';
      
      // Update member status
      await supabase.from('profiles').update({ membership_status: newStatus }).eq('id', member.id);
      
      // If suspending (not reactivating), remove all RSVPs for this member via backend
      if (newStatus === 'suspended') {
        try {
          const response = await fetch(`${API_URL}/api/members/remove-rsvps`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: member.user_id })
          });
          const data = await response.json();
          console.log(`Removed RSVPs for suspended member ${member.full_name}:`, data);
        } catch (rsvpError) {
          console.error('Failed to remove RSVPs:', rsvpError);
        }
      }
      
      await fetchMembers();
      if (selectedMember?.id === member.id) setSelectedMember({ ...selectedMember, membership_status: newStatus });
    } catch (error) { 
      console.error('Failed to update member status:', error);
      alert('Failed to update member status'); 
    }
    setActionLoading(null);
  };

  const handleMakeAdmin = async (member) => {
    setActionLoading(member.id);
    try {
      const newAdminStatus = !member.admin_role;
      await supabase.from('profiles').update({ admin_role: newAdminStatus }).eq('id', member.id);
      await fetchMembers();
      if (selectedMember?.id === member.id) setSelectedMember({ ...selectedMember, admin_role: newAdminStatus });
    } catch (error) { alert('Failed to update admin status'); }
    setActionLoading(null);
  };

  // Event CRUD handlers
  const openEventModal = (event = null) => {
    setEditingItem(event);
    setEventForm(event ? { ...event } : { 
      title: '', description: '', preview_description: '', event_date: '', event_time: '', 
      venue: '', category: 'Networking', image_url: '', max_attendees: 50, is_published: true 
    });
    setShowEventModal(true);
  };

  const saveEvent = async () => {
    setActionLoading('event');
    try {
      // Prepare the form data (remove the publish_to_eventbrite flag before saving)
      const { publish_to_eventbrite, ...formDataToSave } = eventForm;
      
      if (editingItem) {
        await supabase.from('events').update(formDataToSave).eq('id', editingItem.id);
      } else {
        // If publishing to Eventbrite, do that first
        if (publish_to_eventbrite) {
          try {
            const eventbriteResponse = await fetch(`${API_URL}/api/eventbrite/publish`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                title: eventForm.title,
                description: eventForm.description || eventForm.preview_description || '',
                event_date: eventForm.event_date,
                event_time: eventForm.event_time?.replace(/\s*(AM|PM)/i, '') || '18:00',
                end_time: '21:00',
                venue: eventForm.venue || 'San Antonio, TX',
                venue_address: eventForm.venue || 'San Antonio, TX',
                capacity: eventForm.max_attendees || 100,
                image_url: eventForm.image_url || formDataToSave.image_url || null
              })
            });
            
            const eventbriteData = await eventbriteResponse.json();
            
            if (eventbriteData.status === 'success') {
              formDataToSave.eventbrite_url = eventbriteData.eventbrite_url;
              formDataToSave.eventbrite_event_id = eventbriteData.eventbrite_event_id;
            } else {
              console.error('Eventbrite publish failed:', eventbriteData);
            }
          } catch (ebError) {
            console.error('Eventbrite error:', ebError);
          }
        }
        
        await supabase.from('events').insert(formDataToSave);
        
        // Send email notification to all members about the new event
        try {
          await fetch(`${API_URL}/api/email/new-event-notification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event_title: eventForm.title,
              event_date: eventForm.event_date,
              event_time: eventForm.event_time || 'TBA',
              venue: eventForm.venue || 'TBA',
              description: eventForm.preview_description || eventForm.description || '',
              event_type: 'event',
              portal_url: `${window.location.origin}/events`,
              image_url: eventForm.image_url || formDataToSave.image_url || null
            })
          });
        } catch (notifyError) {
          console.error('Failed to send new event notifications:', notifyError);
        }
      }
      await fetchEvents();
      setShowEventModal(false);
    } catch (error) { 
      console.error(error);
      alert('Failed to save event'); 
    }
    setActionLoading(null);
  };

  const deleteEvent = async (id) => {
    console.log('Delete event called with ID:', id);
    setConfirmModal({
      show: true,
      title: 'Delete Event',
      message: 'Are you sure you want to delete this event? This action cannot be undone.',
      onConfirm: async () => {
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
        console.log('User confirmed deletion, proceeding...');
        setActionLoading(id);
        try {
          const url = `${API_URL}/api/admin/delete-event`;
          console.log('Calling delete endpoint:', url);
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_id: id, event_type: 'events' })
          });
          console.log('Response status:', response.status);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Delete request failed:', response.status, errorText);
            alert(`Failed to delete event: Server returned ${response.status}`);
            setActionLoading(null);
            return;
          }
          
          const data = await response.json();
          console.log('Response data:', data);
          if (data.status === 'success') {
            console.log('Delete successful, refreshing events');
            await fetchEvents();
          } else {
            console.error('Delete failed:', data);
            alert(`Failed to delete event: ${data.detail || 'Unknown error'}`);
          }
        } catch (error) { 
          console.error('Delete exception:', error);
          alert('Failed to delete event: ' + error.message); 
        }
        setActionLoading(null);
      }
    });
  };

  // Dinner Club CRUD handlers
  const openDinnerModal = (event = null) => {
    setEditingItem(event);
    setDinnerForm(event ? { ...event } : { 
      title: '', description: '', event_date: '', event_time: '', 
      venue: '', category: 'Networking', image_url: '', max_attendees: 20, price: 150 
    });
    setShowDinnerModal(true);
  };

  const saveDinnerEvent = async () => {
    setActionLoading('dinner');
    try {
      if (editingItem) {
        await supabase.from('dinner_club_events').update(dinnerForm).eq('id', editingItem.id);
      } else {
        await supabase.from('dinner_club_events').insert(dinnerForm);
        
        // Send email notification to all members about the new dinner event
        try {
          await fetch(`${API_URL}/api/email/new-event-notification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event_title: dinnerForm.title,
              event_date: dinnerForm.event_date,
              event_time: dinnerForm.event_time || 'TBA',
              venue: dinnerForm.venue || 'TBA',
              description: dinnerForm.description || '',
              event_type: 'dinner_club',
              portal_url: `${window.location.origin}/dinner-club`,
              image_url: dinnerForm.image_url || null
            })
          });
        } catch (notifyError) {
          console.error('Failed to send new dinner event notifications:', notifyError);
        }
      }
      await fetchDinnerEvents();
      setShowDinnerModal(false);
    } catch (error) { 
      console.error(error);
      alert('Failed to save dinner event'); 
    }
    setActionLoading(null);
  };

  const deleteDinnerEvent = async (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Dinner Event',
      message: 'Are you sure you want to delete this dinner event? This will also delete all RSVPs and payment records for this event.',
      onConfirm: async () => {
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
        setActionLoading(id);
        try {
          const response = await fetch(`${API_URL}/api/admin/delete-event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_id: id, event_type: 'dinner_club_events' })
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Delete dinner event failed:', response.status, errorText);
            alert(`Failed to delete dinner event: Server returned ${response.status}`);
            setActionLoading(null);
            return;
          }
          
          const data = await response.json();
          if (data.status === 'success') {
            await fetchDinnerEvents();
          } else {
            alert(`Failed to delete dinner event: ${data.detail || 'Unknown error'}`);
          }
        } catch (error) { 
          console.error('Delete error:', error);
          alert('Failed to delete dinner event: ' + (error.message || 'Unknown error')); 
        }
        setActionLoading(null);
      }
    });
  };

  // Book Club CRUD handlers
  const openBookModal = (book = null) => {
    setEditingItem(book);
    setBookForm(book ? { ...book } : { title: '', author: '', photo_url: '', chapters_per_week: 2, is_current: false });
    setShowBookModal(true);
  };

  const saveBook = async () => {
    setActionLoading('book');
    try {
      if (bookForm.is_current) {
        await supabase.from('book_club_books').update({ is_current: false }).eq('is_current', true);
      }
      if (editingItem) {
        await supabase.from('book_club_books').update(bookForm).eq('id', editingItem.id);
      } else {
        await supabase.from('book_club_books').insert(bookForm);
      }
      await fetchBookClubData();
      setShowBookModal(false);
    } catch (error) { 
      console.error(error);
      alert('Failed to save book'); 
    }
    setActionLoading(null);
  };

  const deleteBook = async (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Book',
      message: 'Are you sure you want to delete this book?',
      onConfirm: async () => {
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
        setActionLoading(id);
        try {
          const response = await fetch(`${API_URL}/api/admin/delete-event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_id: id, event_type: 'book_club_books' })
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('Delete book failed:', response.status, errorText);
            alert(`Failed to delete book: Server returned ${response.status}`);
            setActionLoading(null);
            return;
          }
          
          const data = await response.json();
          if (data.status === 'success') {
            await fetchBookClubData();
          } else {
            alert(`Failed to delete book: ${data.detail || 'Unknown error'}`);
          }
        } catch (error) { 
          console.error('Delete error:', error);
          alert('Failed to delete book'); 
        }
        setActionLoading(null);
      }
    });
  };

  // Venue CRUD handlers
  const openVenueModal = (venue = null) => {
    setEditingItem(venue);
    setVenueForm(venue ? { ...venue } : { venue_name: '', address: '', meeting_date: '', meeting_time: '10:00', is_next: false, image_url: '' });
    setShowVenueModal(true);
  };

  const saveVenue = async () => {
    setActionLoading('venue');
    try {
      // Extract the publish_to_eventbrite flag before saving
      const { publish_to_eventbrite, ...venueDataToSave } = venueForm;
      
      if (venueDataToSave.is_next) {
        await supabase.from('book_club_venues').update({ is_next: false }).eq('is_next', true);
      }
      
      let venueId;
      if (editingItem) {
        await supabase.from('book_club_venues').update(venueDataToSave).eq('id', editingItem.id);
        venueId = editingItem.id;
      } else {
        const { data, error: venueInsertError } = await supabase.from('book_club_venues').insert(venueDataToSave).select();
        console.log('Venue insert result:', { data, error: venueInsertError });
        if (venueInsertError) {
          throw new Error(`Failed to create venue: ${venueInsertError.message}`);
        }
        venueId = data?.[0]?.id;
        if (!venueId) {
          throw new Error('Failed to get venue ID after insert');
        }
      }
      
      // Sync to Events table
      const eventData = {
        title: `Book Club: ${venueDataToSave.venue_name}`,
        description: `Join us for our weekly Book Club meeting at ${venueDataToSave.venue_name}. ${venueDataToSave.address ? `Location: ${venueDataToSave.address}` : ''}`,
        preview_description: 'Weekly Book Club meeting - coffee and great discussion!',
        event_date: venueDataToSave.meeting_date,
        event_time: venueDataToSave.meeting_time || '10:00 AM',
        venue: venueDataToSave.address || venueDataToSave.venue_name,
        category: 'Book Club',
        image_url: venueDataToSave.image_url || null,
        max_attendees: 30,
        is_published: true,
        book_club_venue_id: venueId
      };
      
      console.log('Attempting to sync event with data:', eventData);
      
      // Check if there's already a synced event for this venue
      const { data: existingEvent } = await supabase
        .from('events')
        .select('id')
        .eq('book_club_venue_id', venueId)
        .maybeSingle();
      
      const isNewVenue = !editingItem;
      console.log('Is new venue:', isNewVenue, 'Existing event found:', !!existingEvent);
      
      if (existingEvent) {
        // Update existing event
        const { data: updateData, error: updateError } = await supabase.from('events').update(eventData).eq('id', existingEvent.id).select();
        console.log('Event update result:', { data: updateData, error: updateError });
      } else {
        // Create new event
        const { data: insertData, error: insertError } = await supabase.from('events').insert(eventData).select();
        console.log('Event insert result:', { data: insertData, error: insertError });
        
        // Send email notification for new Book Club venue
        if (isNewVenue && !insertError) {
          console.log('Sending new Book Club event notification...');
          try {
            const notifyResponse = await fetch(`${API_URL}/api/email/new-event-notification`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                event_title: `Book Club: ${venueDataToSave.venue_name}`,
                event_date: venueDataToSave.meeting_date,
                event_time: venueDataToSave.meeting_time || '10:00 AM',
                venue: venueDataToSave.address || venueDataToSave.venue_name,
                description: 'Weekly Book Club meeting - coffee and great discussion!',
                event_type: 'event',
                portal_url: `${window.location.origin}/book-club`,
                image_url: venueDataToSave.image_url || null
              })
            });
            const notifyResult = await notifyResponse.json();
            console.log('Book Club notification result:', notifyResult);
          } catch (notifyError) {
            console.error('Failed to send new Book Club event notifications:', notifyError);
          }
        } else if (insertError) {
          console.error('Event insert failed, notification not sent:', insertError);
        }
      }
      
      // Publish to Eventbrite if checked (only for new venues)
      if (publish_to_eventbrite && !editingItem) {
        try {
          const eventbriteResponse = await fetch(`${API_URL}/api/eventbrite/publish`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: `Book Club: ${venueDataToSave.venue_name}`,
              description: `Join us for our weekly Book Club meeting at ${venueDataToSave.venue_name}. Intentional networking with like-minded professionals.`,
              event_date: venueDataToSave.meeting_date,
              event_time: venueDataToSave.meeting_time?.replace(/\s*(AM|PM)/i, '') || '10:00',
              end_time: '12:00',
              venue: venueDataToSave.venue_name,
              venue_address: venueDataToSave.address || 'San Antonio, TX',
              capacity: 30,
              image_url: venueDataToSave.image_url || null
            })
          });
          
          const eventbriteData = await eventbriteResponse.json();
          
          if (eventbriteData.status === 'success') {
            // Update venue with Eventbrite URL
            await supabase.from('book_club_venues').update({
              eventbrite_url: eventbriteData.eventbrite_url,
              eventbrite_event_id: eventbriteData.eventbrite_event_id
            }).eq('id', venueId);
            
            alert(`Book Club meeting published to Eventbrite!\n\nURL: ${eventbriteData.eventbrite_url}`);
          } else {
            console.error('Eventbrite publish failed:', eventbriteData);
            alert(`Failed to publish to Eventbrite: ${eventbriteData.detail || 'Unknown error'}\n\nVenue will still be saved locally.`);
          }
        } catch (ebError) {
          console.error('Eventbrite error:', ebError);
          alert('Failed to connect to Eventbrite. Venue will still be saved locally.');
        }
      }
      
      await Promise.all([fetchBookClubData(), fetchEvents()]);
      setShowVenueModal(false);
    } catch (error) { 
      console.error(error);
      alert('Failed to save venue'); 
    }
    setActionLoading(null);
  };

  const deleteVenue = async (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Venue',
      message: 'Are you sure you want to delete this venue? This will also delete the synced event.',
      onConfirm: async () => {
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
        setActionLoading(id);
        try {
          // Delete synced event first (via backend to bypass RLS)
          const { data: syncedEvent } = await supabase.from('events').select('id').eq('book_club_venue_id', id).maybeSingle();
          if (syncedEvent) {
            const eventResponse = await fetch(`${API_URL}/api/admin/delete-event`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ event_id: syncedEvent.id, event_type: 'events' })
            });
            const eventResult = await eventResponse.json();
            console.log('Synced event delete result:', eventResult);
          }
          
          // Then delete the venue via backend API
          const response = await fetch(`${API_URL}/api/admin/delete-event`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ event_id: id, event_type: 'book_club_venues' })
          });
          const data = await response.json();
          console.log('Venue delete result:', data);
          
          if (data.status === 'success') {
            await Promise.all([fetchBookClubData(), fetchEvents()]);
          } else {
            alert(`Failed to delete venue: ${data.detail || 'Unknown error'}`);
          }
        } catch (error) { 
          console.error(error);
          alert('Failed to delete venue: ' + (error.message || 'Unknown error')); 
        }
        setActionLoading(null);
      }
    });
  };

  const openMemberDetails = async (member) => { 
    setSelectedMember(member); 
    setMemberApplication(null);
    setShowMemberModal(true);
    
    // Fetch application data for this member
    if (member.user_id) {
      const { data } = await supabase
        .from('applications')
        .select('*')
        .eq('user_id', member.user_id)
        .maybeSingle();
      if (data) setMemberApplication(data);
    }
  };

  // Image Upload Component
  const ImageUploader = ({ currentUrl, onUpload, uploading, label }) => (
    <div>
      <label className="block text-sm font-medium text-gray-400 mb-2">{label}</label>
      <div className="flex gap-4 items-start">
        <div className="relative">
          {currentUrl ? (
            <img src={currentUrl} alt="Preview" className="w-32 h-32 rounded-xl object-cover border-2 border-brand-gold/30" />
          ) : (
            <div className="w-32 h-32 rounded-xl bg-black/30 border-2 border-dashed border-brand-gold/30 flex items-center justify-center">
              <Image size={32} className="text-gray-500" />
            </div>
          )}
          <label className="absolute -bottom-2 -right-2 w-10 h-10 bg-brand-gold rounded-xl flex items-center justify-center cursor-pointer hover:bg-yellow-400 transition-colors">
            <Camera size={18} className="text-black" />
            <input type="file" accept="image/*" onChange={onUpload} className="hidden" disabled={uploading} />
          </label>
        </div>
        <div className="flex-1">
          <p className="text-gray-500 text-xs mb-2">Click the camera icon to upload an image, or enter a URL below:</p>
          <input 
            type="text" 
            value={currentUrl || ''} 
            onChange={e => {
              if (label.includes('Event')) setEventForm({...eventForm, image_url: e.target.value});
              else if (label.includes('Dinner')) setDinnerForm({...dinnerForm, image_url: e.target.value});
              else if (label.includes('Book')) setBookForm({...bookForm, photo_url: e.target.value});
            }}
            placeholder="https://... (optional)"
            className="w-full px-4 py-2 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors text-sm"
          />
          {uploading && <p className="text-brand-gold text-xs mt-2">Uploading image...</p>}
        </div>
      </div>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-gold"></div></div>;

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto px-4 py-12 text-center">
        <div className="glass-card rounded-2xl p-8">
          <Shield size={48} className="mx-auto text-red-400 mb-4" />
          <h1 className="text-2xl font-bold gradient-text mb-4">Access Denied</h1>
          <p className="text-gray-400 mb-8">You don't have permission to access the admin panel.</p>
          <button onClick={() => navigate('/')} className="btn-primary px-6 py-3 rounded-xl">Go to Events</button>
        </div>
      </div>
    );
  }

  const pendingApplications = applications.filter(a => a.status === 'pending');
  const activeMembers = members.filter(m => m.membership_status === 'active');
  const suspendedMembers = members.filter(m => m.membership_status === 'suspended');
  const filteredMembers = members.filter(m => {
    const matchesSearch = !searchQuery || m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || m.company?.toLowerCase().includes(searchQuery.toLowerCase()) || m.role?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || m.membership_status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  const filteredApplications = applications.filter(a => {
    const matchesSearch = !searchQuery || a.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || a.company?.toLowerCase().includes(searchQuery.toLowerCase()) || a.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (d) => {
    if (!d) return '-';
    // Handle both date-only strings (YYYY-MM-DD) and full timestamps
    if (d.includes('T') || d.includes(' ')) {
      return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    // Parse date-only as local time to avoid timezone shift
    const [year, month, day] = d.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button onClick={() => navigate('/')} className="flex items-center text-gray-400 hover:text-brand-gold mb-6 transition-colors">
        <ArrowLeft size={20} className="mr-2" /> Back to Events
      </button>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <h1 className="text-3xl font-bold gradient-text mb-4 md:mb-0">Admin Dashboard</h1>
        <div className="flex items-center gap-3">
          <span className="px-4 py-2 rounded-xl bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-semibold">{activeMembers.length} Active</span>
          <span className="px-4 py-2 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm font-semibold">{pendingApplications.length} Pending</span>
          {suspendedMembers.length > 0 && <span className="px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-semibold">{suspendedMembers.length} Suspended</span>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[{ icon: Users, value: activeMembers.length, label: 'Active Members', color: 'from-green-500/20 to-emerald-500/20', iconColor: 'text-green-400' },
          { icon: Clock, value: pendingApplications.length, label: 'Pending Apps', color: 'from-yellow-500/20 to-orange-500/20', iconColor: 'text-yellow-400' },
          { icon: Calendar, value: events.length, label: 'Events', color: 'from-blue-500/20 to-cyan-500/20', iconColor: 'text-blue-400' },
          { icon: Utensils, value: dinnerEvents.length, label: 'Dinner Events', color: 'from-brand-gold/20 to-yellow-500/20', iconColor: 'text-brand-gold' },
        ].map(({ icon: Icon, value, label, color, iconColor }, idx) => (
          <div key={idx} className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${color}`}>
            <Icon size={24} className={`${iconColor} mb-3`} />
            <p className="text-3xl font-bold text-white">{value}</p>
            <p className="text-sm text-gray-400">{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[{ key: 'applications', label: 'Applications', icon: Clock, count: pendingApplications.length },
          { key: 'members', label: 'Manage Members', icon: Users, count: members.length },
          { key: 'events', label: 'Events', icon: Calendar, count: events.length },
          { key: 'dinner', label: 'Dinner Club', icon: Utensils, count: dinnerEvents.length },
          { key: 'book', label: 'Book Club', icon: BookOpen },
          { key: 'settings', label: 'Settings', icon: Settings },
        ].map(({ key, label, icon: Icon, count }) => (
          <button key={key} onClick={() => { setActiveTab(key); setSearchQuery(''); setStatusFilter('all'); }}
            className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${activeTab === key ? 'bg-gradient-to-r from-brand-gold to-yellow-500 text-black shadow-lg shadow-brand-gold/25' : 'glass-card text-gray-400 hover:text-white'}`}>
            <Icon size={18} />{label}{count !== undefined && <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === key ? 'bg-black/20' : 'bg-white/10'}`}>{count}</span>}
          </button>
        ))}
      </div>

      {/* Search & Filters for Applications/Members */}
      {(activeTab === 'members' || activeTab === 'applications') && (
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
            <input type="text" placeholder={activeTab === 'members' ? 'Search members...' : 'Search applications...'} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-black/30 border border-brand-gold/20 text-white placeholder-gray-500 focus:border-brand-gold transition-colors" />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-500" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-3 rounded-xl bg-black/30 border border-brand-gold/20 text-white focus:border-brand-gold transition-colors cursor-pointer">
              <option value="all">All Status</option>
              {activeTab === 'members' ? (<><option value="active">Active</option><option value="suspended">Suspended</option><option value="inactive">Inactive</option></>) : (<><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option></>)}
            </select>
          </div>
        </div>
      )}

      {dataLoading ? <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-gold"></div></div> : (
        <>
          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div className="space-y-4">
              {filteredApplications.length === 0 ? (
                <div className="glass-card rounded-2xl p-12 text-center"><Clock size={48} className="mx-auto text-brand-gold mb-4" /><p className="text-gray-400">{searchQuery ? 'No applications match your search' : 'No applications yet'}</p></div>
              ) : filteredApplications.map((app) => (
                <div key={app.id} className="glass-card rounded-2xl overflow-hidden card-hover">
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      <img src={app.headshot_url} alt={app.full_name} className="w-16 h-16 rounded-xl object-cover border-2 border-brand-gold/30" />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <h3 className="text-lg font-bold text-white">{app.full_name}</h3>
                          <span className={`px-3 py-1 rounded-lg text-xs font-bold ${app.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : app.status === 'approved' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>{app.status?.toUpperCase()}</span>
                          <span className={`px-3 py-1 rounded-lg text-xs font-bold ${app.desired_tier === 'founder' ? 'bg-brand-gold text-black' : 'bg-brand-gold/20 text-brand-gold border border-brand-gold/30'}`}>{app.desired_tier?.toUpperCase()}</span>
                        </div>
                        <p className="text-brand-gold text-sm">{app.profession}</p>
                        <p className="text-gray-400 text-sm">{app.company}</p>
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500"><span className="flex items-center gap-1"><Mail size={12} /> {app.email}</span><span>Applied: {formatDate(app.applied_at)}</span></div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {app.status === 'pending' && (<>
                          <button onClick={() => handleApprove(app)} disabled={actionLoading === app.id} className="px-4 py-2 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"><CheckCircle size={18} />{actionLoading === app.id ? '...' : 'Approve'}</button>
                          <button onClick={() => handleReject(app)} disabled={actionLoading === app.id} className="px-4 py-2 bg-red-500/20 text-red-400 font-semibold rounded-xl hover:bg-red-500/30 disabled:opacity-50 flex items-center gap-2 border border-red-500/30"><XCircle size={18} />{actionLoading === app.id ? '...' : 'Reject'}</button>
                        </>)}
                        <button onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)} className="px-4 py-2 bg-white/5 text-gray-400 font-semibold rounded-xl hover:bg-white/10 flex items-center gap-2 border border-white/10"><Eye size={18} />{expandedApp === app.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
                      </div>
                    </div>
                  </div>
                  {expandedApp === app.id && (
                    <div className="px-6 pb-6 pt-4 border-t border-brand-gold/10 space-y-6">
                      {/* Contact Information */}
                      <div>
                        <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Contact Information</h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="p-3 rounded-xl bg-black/20">
                            <h4 className="text-xs font-semibold text-gray-500 mb-1">Email</h4>
                            <p className="text-gray-300 text-sm flex items-center gap-2"><Mail size={14} className="text-brand-gold" />{app.email || 'Not provided'}</p>
                          </div>
                          <div className="p-3 rounded-xl bg-black/20">
                            <h4 className="text-xs font-semibold text-gray-500 mb-1">Phone Number</h4>
                            <p className="text-gray-300 text-sm flex items-center gap-2"><Phone size={14} className="text-brand-gold" />{app.phone_number || 'Not provided'}</p>
                          </div>
                          <div className="p-3 rounded-xl bg-black/20 flex gap-4">
                            {app.linkedin_url && <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm"><Linkedin size={16} /> LinkedIn</a>}
                            {app.instagram_url && <a href={app.instagram_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-pink-400 hover:text-pink-300 text-sm"><Instagram size={16} /> Instagram</a>}
                            {!app.linkedin_url && !app.instagram_url && <span className="text-gray-500 text-sm">No social links</span>}
                          </div>
                        </div>
                      </div>

                      {/* Professional Background */}
                      <div>
                        <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Professional Background</h3>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div className="p-3 rounded-xl bg-black/20">
                            <h4 className="text-xs font-semibold text-gray-500 mb-1">Profession/Title</h4>
                            <p className="text-gray-300 text-sm">{app.profession || 'Not provided'}</p>
                          </div>
                          <div className="p-3 rounded-xl bg-black/20">
                            <h4 className="text-xs font-semibold text-gray-500 mb-1">Company</h4>
                            <p className="text-gray-300 text-sm">{app.company || 'Not provided'}</p>
                          </div>
                          <div className="p-3 rounded-xl bg-black/20">
                            <h4 className="text-xs font-semibold text-gray-500 mb-1">Industry</h4>
                            <p className="text-gray-300 text-sm">{app.industry || 'Not provided'}</p>
                          </div>
                          <div className="p-3 rounded-xl bg-black/20">
                            <h4 className="text-xs font-semibold text-gray-500 mb-1">Years of Experience</h4>
                            <p className="text-gray-300 text-sm">{app.years_of_experience || 'Not provided'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Education & Credentials */}
                      {app.education_credentials && (
                        <div>
                          <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Education & Credentials</h3>
                          <div className="p-3 rounded-xl bg-black/20">
                            <p className="text-gray-300 text-sm whitespace-pre-wrap">{app.education_credentials}</p>
                          </div>
                        </div>
                      )}

                      {/* Areas of Interest */}
                      {app.areas_of_interest && app.areas_of_interest.length > 0 && (
                        <div>
                          <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Areas of Interest</h3>
                          <div className="flex flex-wrap gap-2">
                            {app.areas_of_interest.map((interest, idx) => (
                              <span key={idx} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-brand-gold/10 text-brand-gold border border-brand-gold/20">{interest}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Application Responses */}
                      <div>
                        <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Application Responses</h3>
                        <div className="space-y-4">
                          <div className="p-4 rounded-xl bg-black/20">
                            <h4 className="text-sm font-semibold text-brand-gold mb-2">Why do you want to join?</h4>
                            <p className="text-gray-300 text-sm whitespace-pre-wrap">{app.why_join || 'Not provided'}</p>
                          </div>
                          <div className="p-4 rounded-xl bg-black/20">
                            <h4 className="text-sm font-semibold text-brand-gold mb-2">What value can you bring?</h4>
                            <p className="text-gray-300 text-sm whitespace-pre-wrap">{app.contribution || 'Not provided'}</p>
                          </div>
                          <div className="p-4 rounded-xl bg-black/20">
                            <h4 className="text-sm font-semibold text-brand-gold mb-2">Networking Goals</h4>
                            <p className="text-gray-300 text-sm whitespace-pre-wrap">{app.networking_goals || 'Not provided'}</p>
                          </div>
                          {app.notable_achievements && (
                            <div className="p-4 rounded-xl bg-black/20">
                              <h4 className="text-sm font-semibold text-brand-gold mb-2">Notable Achievements</h4>
                              <p className="text-gray-300 text-sm whitespace-pre-wrap">{app.notable_achievements}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div>
                        <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Additional Information</h3>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div className="p-3 rounded-xl bg-black/20">
                            <h4 className="text-xs font-semibold text-gray-500 mb-1">How did they hear about us?</h4>
                            <p className="text-gray-300 text-sm">{app.how_heard || 'Not provided'}</p>
                          </div>
                          <div className="p-3 rounded-xl bg-black/20">
                            <h4 className="text-xs font-semibold text-gray-500 mb-1">Desired Tier</h4>
                            <p className="text-gray-300 text-sm">{app.desired_tier?.toUpperCase() || 'Not specified'}</p>
                          </div>
                          <div className="p-3 rounded-xl bg-black/20">
                            <h4 className="text-xs font-semibold text-gray-500 mb-1">Applied On</h4>
                            <p className="text-gray-300 text-sm">{formatDate(app.applied_at)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Members Tab */}
          {activeTab === 'members' && (
            <div className="space-y-3">
              {filteredMembers.length === 0 ? <div className="glass-card rounded-2xl p-12 text-center"><Users size={48} className="mx-auto text-brand-gold mb-4" /><p className="text-gray-400">{searchQuery ? 'No members match your search' : 'No members yet'}</p></div> : (<>
                <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider"><div className="col-span-4">Member</div><div className="col-span-2">Tier</div><div className="col-span-2">Status</div><div className="col-span-2">Joined</div><div className="col-span-2 text-right">Actions</div></div>
                {filteredMembers.map((member) => (
                  <div key={member.id} className={`glass-card rounded-xl p-4 md:p-6 card-hover ${member.membership_status === 'suspended' ? 'border-red-500/30 bg-red-500/5' : ''}`}>
                    <div className="grid md:grid-cols-12 gap-4 items-center">
                      <div className="md:col-span-4 flex items-center gap-4">
                        <img src={member.headshot_url} alt={member.full_name} className="w-12 h-12 rounded-xl object-cover border-2 border-brand-gold/30" />
                        <div className="min-w-0"><div className="flex items-center gap-2"><h3 className="font-bold text-white truncate">{member.full_name}</h3>{member.admin_role && <Shield size={14} className="text-brand-gold flex-shrink-0" title="Admin" />}</div><p className="text-brand-gold text-sm truncate">{member.role}</p><p className="text-gray-400 text-xs truncate">{member.company}</p></div>
                      </div>
                      <div className="md:col-span-2"><span className={`inline-flex px-3 py-1 rounded-lg text-xs font-bold ${member.membership_tier === 'founder' ? 'bg-brand-gold text-black' : 'bg-brand-gold/20 text-brand-gold border border-brand-gold/30'}`}>{member.membership_tier?.toUpperCase()}</span></div>
                      <div className="md:col-span-2"><span className={`inline-flex px-3 py-1 rounded-lg text-xs font-bold ${member.membership_status === 'active' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : member.membership_status === 'suspended' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'}`}>{member.membership_status?.toUpperCase()}</span></div>
                      <div className="md:col-span-2 text-gray-400 text-sm">{formatDate(member.created_at)}</div>
                      <div className="md:col-span-2 flex items-center justify-end gap-2">
                        <button onClick={() => openMemberDetails(member)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors" title="View Details"><Eye size={18} /></button>
                        <button onClick={() => handleSuspend(member)} disabled={actionLoading === member.id} className={`p-2 rounded-lg transition-colors ${member.membership_status === 'suspended' ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400' : 'bg-red-500/20 hover:bg-red-500/30 text-red-400'}`} title={member.membership_status === 'suspended' ? 'Reactivate' : 'Suspend'}>{member.membership_status === 'suspended' ? <UserCheck size={18} /> : <Ban size={18} />}</button>
                        <button onClick={() => handleMakeAdmin(member)} disabled={actionLoading === member.id} className={`p-2 rounded-lg transition-colors ${member.admin_role ? 'bg-brand-gold/20 hover:bg-brand-gold/30 text-brand-gold' : 'bg-white/5 hover:bg-white/10 text-gray-400'}`} title={member.admin_role ? 'Remove Admin' : 'Make Admin'}><Shield size={18} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </>)}
            </div>
          )}

          {/* Events Tab */}
          {activeTab === 'events' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Manage Events</h2>
                <button onClick={() => openEventModal()} className="btn-primary px-5 py-2.5 rounded-xl flex items-center gap-2"><Plus size={18} /> Add Event</button>
              </div>
              {events.length === 0 ? <div className="glass-card rounded-2xl p-12 text-center"><Calendar size={48} className="mx-auto text-brand-gold mb-4" /><p className="text-gray-400">No events yet. Create your first event!</p></div> : (
                <div className="space-y-4">
                  {events.map(event => (
                    <div key={event.id} className="glass-card rounded-xl p-5 flex items-center gap-4">
                      {event.image_url ? <img src={event.image_url} alt="" className="w-20 h-20 rounded-xl object-cover" /> : <div className="w-20 h-20 rounded-xl bg-brand-gold/10 flex items-center justify-center"><Image size={24} className="text-brand-gold/50" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1"><h3 className="font-bold text-white truncate">{event.title}</h3>{!event.is_published && <span className="px-2 py-0.5 rounded text-xs bg-gray-500/20 text-gray-400">Draft</span>}{event.category && <span className="px-2 py-0.5 rounded text-xs bg-brand-gold/20 text-brand-gold">{event.category}</span>}</div>
                        <p className="text-gray-400 text-sm">{formatDate(event.event_date)} • {event.event_time}</p>
                        <p className="text-gray-500 text-xs">{event.venue}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openEventModal(event)} className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"><Edit2 size={18} /></button>
                        <button onClick={() => deleteEvent(event.id)} disabled={actionLoading === event.id} className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 disabled:opacity-50"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Dinner Club Tab */}
          {activeTab === 'dinner' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-white">Manage Dinner Club Events</h2>
                <button onClick={() => openDinnerModal()} className="btn-primary px-5 py-2.5 rounded-xl flex items-center gap-2"><Plus size={18} /> Add Dinner Event</button>
              </div>
              {dinnerEvents.length === 0 ? <div className="glass-card rounded-2xl p-12 text-center"><Utensils size={48} className="mx-auto text-brand-gold mb-4" /><p className="text-gray-400">No dinner events yet. Create your first dinner event!</p></div> : (
                <div className="space-y-4">
                  {dinnerEvents.map(event => (
                    <div key={event.id} className="glass-card rounded-xl p-5 flex items-center gap-4">
                      {event.image_url ? <img src={event.image_url} alt="" className="w-20 h-20 rounded-xl object-cover" /> : <div className="w-20 h-20 rounded-xl bg-brand-gold/10 flex items-center justify-center"><Utensils size={24} className="text-brand-gold/50" /></div>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1"><h3 className="font-bold text-white truncate">{event.title}</h3><span className="px-2 py-0.5 rounded text-xs bg-brand-gold text-black font-bold">${event.price}</span><span className="px-2 py-0.5 rounded text-xs bg-brand-gold/20 text-brand-gold">{event.category}</span></div>
                        <p className="text-gray-400 text-sm">{formatDate(event.event_date)} • {event.event_time}</p>
                        <p className="text-gray-500 text-xs">{event.venue} • Max {event.max_attendees} attendees</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => openDinnerModal(event)} className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"><Edit2 size={18} /></button>
                        <button onClick={() => deleteDinnerEvent(event.id)} disabled={actionLoading === event.id} className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 disabled:opacity-50"><Trash2 size={18} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Book Club Tab */}
          {activeTab === 'book' && (
            <div className="space-y-8">
              {/* Books Section */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">Books</h2>
                  <button onClick={() => openBookModal()} className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2"><Plus size={18} /> Add Book</button>
                </div>
                {bookClubData.books.length === 0 ? <div className="glass-card rounded-xl p-8 text-center"><BookOpen size={32} className="mx-auto text-brand-gold mb-2" /><p className="text-gray-400 text-sm">No books added yet</p></div> : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {bookClubData.books.map(book => (
                      <div key={book.id} className={`glass-card rounded-xl p-4 flex items-center gap-4 ${book.is_current ? 'border-green-500/30 bg-green-500/5' : ''}`}>
                        {book.photo_url ? <img src={book.photo_url} alt="" className="w-16 h-20 rounded-lg object-cover" /> : <div className="w-16 h-20 rounded-lg bg-brand-gold/10 flex items-center justify-center"><BookOpen size={20} className="text-brand-gold/50" /></div>}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1"><h3 className="font-bold text-white truncate">{book.title}</h3>{book.is_current && <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">Current</span>}</div>
                          <p className="text-gray-400 text-sm">by {book.author}</p>
                          <p className="text-gray-500 text-xs">{book.chapters_per_week} chapters/week</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => openBookModal(book)} className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"><Edit2 size={16} /></button>
                          <button onClick={() => deleteBook(book.id)} disabled={actionLoading === book.id} className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 disabled:opacity-50"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Venues Section */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold text-white">Meeting Venues</h2>
                  <button onClick={() => openVenueModal()} className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2"><Plus size={18} /> Add Venue</button>
                </div>
                {bookClubData.venues.length === 0 ? <div className="glass-card rounded-xl p-8 text-center"><Calendar size={32} className="mx-auto text-brand-gold mb-2" /><p className="text-gray-400 text-sm">No venues added yet</p></div> : (
                  <div className="space-y-3">
                    {bookClubData.venues.map(venue => (
                      <div key={venue.id} className={`glass-card rounded-xl p-4 flex items-center gap-4 ${venue.is_next ? 'border-green-500/30 bg-green-500/5' : ''}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1"><h3 className="font-bold text-white">{venue.venue_name}</h3>{venue.is_next && <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">Next Meeting</span>}</div>
                          <p className="text-gray-400 text-sm">{formatDate(venue.meeting_date)} • {venue.meeting_time}</p>
                          <p className="text-gray-500 text-xs">{venue.address}</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => openVenueModal(venue)} className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"><Edit2 size={16} /></button>
                          <button onClick={() => deleteVenue(venue.id)} disabled={actionLoading === venue.id} className="p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 disabled:opacity-50"><Trash2 size={16} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white mb-6">App Settings</h2>
              
              {/* Member Directory Visibility Toggle */}
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Member Directory Visibility</h3>
                    <p className="text-gray-400 text-sm">
                      When enabled, approved members can view other members' profiles in the Members section. 
                      When disabled, the member directory will be hidden from all members.
                    </p>
                  </div>
                  <button
                    onClick={toggleMembersVisibility}
                    disabled={settingsLoading}
                    className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold transition-all duration-300 ${
                      membersVisible 
                        ? 'bg-green-500 text-white hover:bg-green-600' 
                        : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                    } disabled:opacity-50`}
                    data-testid="toggle-members-visibility"
                  >
                    {settingsLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
                    ) : membersVisible ? (
                      <><ToggleRight size={20} /> Visible</>
                    ) : (
                      <><ToggleLeft size={20} /> Hidden</>
                    )}
                  </button>
                </div>
                <div className={`mt-4 p-3 rounded-xl text-sm ${membersVisible ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
                  {membersVisible 
                    ? '✓ Members can currently view the member directory' 
                    : '✗ Member directory is currently hidden from all members'}
                </div>
              </div>

              {/* Email Notifications Info */}
              <div className="glass-card rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-2">Email Notifications</h3>
                <p className="text-gray-400 text-sm mb-4">
                  The following automated emails are configured and sent via Resend from <span className="text-brand-gold">noreply@econclubsa.org</span>:
                </p>
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-black/20 border border-brand-gold/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail size={18} className="text-brand-gold" />
                      <h4 className="font-semibold text-white">Application Submitted</h4>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">Triggered when a new membership application is submitted.</p>
                    <ul className="text-xs text-gray-500 space-y-1 ml-6">
                      <li>• <strong className="text-gray-400">To Applicant:</strong> Confirmation email acknowledging receipt, 5-7 day review timeline</li>
                      <li>• <strong className="text-gray-400">To All Admins:</strong> Notification with applicant name and email for review</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-black/20 border border-brand-gold/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail size={18} className="text-green-400" />
                      <h4 className="font-semibold text-white">Application Approved (Welcome Email)</h4>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">Triggered when an admin approves a membership application.</p>
                    <ul className="text-xs text-gray-500 space-y-1 ml-6">
                      <li>• <strong className="text-gray-400">To New Member:</strong> Welcome message with onboarding steps</li>
                      <li>• Includes: Update profile instructions, RSVP to events, Join Dinner Club, Participate in Book Club</li>
                      <li>• Contains direct link to member portal and member benefits summary</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 rounded-xl bg-black/20 border border-brand-gold/10">
                    <div className="flex items-center gap-2 mb-2">
                      <Bell size={18} className="text-blue-400" />
                      <h4 className="font-semibold text-white">Event Reminders</h4>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">Triggered manually via the button below or via scheduled cron job.</p>
                    <ul className="text-xs text-gray-500 space-y-1 ml-6">
                      <li>• <strong className="text-gray-400">To RSVPed Members:</strong> Reminder 24 hours before events</li>
                      <li>• Covers all event types: Events, Dinner Club, and Book Club meetings</li>
                      <li>• Includes event title, date, time, venue, and link to view details</li>
                      <li>• Duplicate prevention: Each reminder is only sent once per user per event</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 p-3 rounded-xl bg-green-500/10 border border-green-500/20">
                  <p className="text-green-400 text-xs flex items-center gap-2">
                    <CheckCircle size={14} />
                    Email service is configured and active
                  </p>
                </div>
              </div>

              {/* Automated Event Reminders */}
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Bell className="text-brand-gold" size={24} />
                  <h3 className="text-lg font-bold text-white">Automated Event Reminders</h3>
                </div>
                
                {/* Scheduler Status */}
                {schedulerStatus && (
                  <div className="mb-4 p-4 bg-black/30 rounded-xl border border-brand-gold/20">
                    <div className="flex items-center gap-2 mb-3">
                      {schedulerStatus.scheduler_running ? (
                        <span className="flex items-center gap-2 text-green-400 text-sm font-medium">
                          <CheckCircle size={16} /> Automation Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-2 text-red-400 text-sm font-medium">
                          <XCircle size={16} /> Automation Inactive
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <p className="text-gray-400">
                        <strong className="text-brand-gold">Event reminders:</strong> Daily at 12:00 PM CST for events happening the next day
                      </p>
                      <p className="text-gray-400 text-xs">
                        Applies to Events and Dinner Club only.
                      </p>
                    </div>
                    
                    {schedulerStatus.jobs && schedulerStatus.jobs.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-brand-gold/10">
                        <p className="text-xs text-gray-500 mb-2">Next scheduled run:</p>
                        <div className="space-y-1">
                          {schedulerStatus.jobs.map(job => (
                            <div key={job.id} className="flex items-center gap-2 text-xs">
                              <Clock size={12} className="text-brand-gold" />
                              <span className="text-gray-400">{job.name}:</span>
                              <span className="text-white">
                                {job.next_run ? new Date(job.next_run).toLocaleString('en-US', { 
                                  timeZone: 'America/Chicago',
                                  month: 'short', 
                                  day: 'numeric', 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true 
                                }) + ' CST' : 'Not scheduled'}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Manual Trigger Buttons */}
                <div className="mb-4">
                  <p className="text-gray-400 text-sm mb-3">
                    Reminders are sent automatically, but you can also trigger them manually:
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => sendEventReminders('24h')}
                      disabled={reminderLoading}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl font-semibold bg-brand-gold text-black hover:bg-brand-gold-dark transition-all duration-300 disabled:opacity-50"
                      data-testid="send-24h-reminders-btn"
                    >
                      {reminderLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent" />
                      ) : (
                        <><Send size={16} /> Send Reminders Now</>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Result Display */}
                {reminderResult && (
                  <div className={`p-3 rounded-xl text-sm ${
                    reminderResult.status === 'completed' && (reminderResult.results?.total_sent > 0 || reminderResult.total_sent > 0)
                      ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                      : reminderResult.status === 'completed' && (reminderResult.results?.total_sent === 0 || reminderResult.total_sent === 0)
                      ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400'
                      : 'bg-red-500/10 border border-red-500/20 text-red-400'
                  }`}>
                    {reminderResult.status === 'completed' 
                      ? (reminderResult.results?.total_sent === 0 || reminderResult.total_sent === 0)
                        ? 'All members have already been notified for upcoming events.'
                        : reminderResult.summary
                      : reminderResult.message || 'An error occurred'}
                  </div>
                )}
              </div>

              {/* Custom Email to Members */}
              <div className="glass-card rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="text-brand-gold" size={24} />
                  <h3 className="text-lg font-bold text-white">Send Custom Email</h3>
                </div>
                
                <p className="text-gray-400 text-sm mb-4">
                  Send a custom email to all approved members. The email will include the ECSA logo and social media links.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Subject</label>
                    <input
                      type="text"
                      value={customEmailSubject}
                      onChange={(e) => setCustomEmailSubject(e.target.value)}
                      placeholder="Enter email subject..."
                      className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold/50"
                      data-testid="custom-email-subject"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Message</label>
                    <textarea
                      value={customEmailMessage}
                      onChange={(e) => setCustomEmailMessage(e.target.value)}
                      placeholder="Enter your message here... (supports line breaks)"
                      rows={6}
                      className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-brand-gold/50 resize-none"
                      data-testid="custom-email-message"
                    />
                  </div>
                  
                  <button
                    onClick={sendCustomEmail}
                    disabled={customEmailLoading || !customEmailSubject.trim() || !customEmailMessage.trim()}
                    className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold bg-brand-gold text-black hover:bg-brand-gold-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="send-custom-email-btn"
                  >
                    {customEmailLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-black border-t-transparent" />
                    ) : (
                      <><Send size={18} /> Send to All Members</>
                    )}
                  </button>
                </div>
                
                {customEmailResult && (
                  <div className={`mt-4 p-3 rounded-xl text-sm ${
                    customEmailResult.status === 'completed' && customEmailResult.results?.total_sent > 0
                      ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                      : customEmailResult.status === 'completed'
                      ? 'bg-blue-500/10 border border-blue-500/20 text-blue-400'
                      : 'bg-red-500/10 border border-red-500/20 text-red-400'
                  }`}>
                    {customEmailResult.status === 'completed' 
                      ? customEmailResult.summary
                      : customEmailResult.message || 'An error occurred'}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Member Details Modal */}
      {showMemberModal && selectedMember && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowMemberModal(false)}>
          <div className="bg-brand-dark-light rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-brand-gold/20 flex items-center justify-between">
              <h3 className="text-xl font-bold gradient-text">Member Details</h3>
              <button onClick={() => setShowMemberModal(false)} className="p-2 rounded-lg hover:bg-white/10"><X size={24} className="text-gray-400" /></button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh] space-y-6">
              {/* Header Section */}
              <div className="flex flex-col sm:flex-row gap-6">
                <img src={selectedMember.headshot_url} alt={selectedMember.full_name} className="w-32 h-32 rounded-2xl object-cover border-2 border-brand-gold/30 mx-auto sm:mx-0" />
                <div className="text-center sm:text-left flex-1">
                  <h2 className="text-2xl font-bold text-white mb-2">{selectedMember.full_name}</h2>
                  <p className="text-brand-gold">{selectedMember.role}</p>
                  <p className="text-gray-400">{selectedMember.company}</p>
                  <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                    <span className={`px-3 py-1 rounded-lg text-xs font-bold ${selectedMember.membership_tier === 'founder' ? 'bg-brand-gold text-black' : 'bg-brand-gold/20 text-brand-gold'}`}>{selectedMember.membership_tier?.toUpperCase()}</span>
                    <span className={`px-3 py-1 rounded-lg text-xs font-bold ${selectedMember.membership_status === 'active' ? 'bg-green-500/20 text-green-400' : selectedMember.membership_status === 'suspended' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>{selectedMember.membership_status?.toUpperCase()}</span>
                    {selectedMember.admin_role && <span className="px-3 py-1 rounded-lg text-xs font-bold bg-blue-500/20 text-blue-400">ADMIN</span>}
                    {selectedMember.member_number && <span className="px-3 py-1 rounded-lg text-xs font-bold bg-gray-500/20 text-gray-400">#{selectedMember.member_number}</span>}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Contact Information</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-black/20">
                    <Mail size={18} className="text-brand-gold" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-gray-300 text-sm">{selectedMember.email || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-black/20">
                    <Phone size={18} className="text-brand-gold" />
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="text-gray-300 text-sm">{selectedMember.phone_number || 'Not provided'}</p>
                    </div>
                  </div>
                  {selectedMember.linkedin_url && (
                    <a href={selectedMember.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-black/20 hover:bg-black/30">
                      <Linkedin size={18} className="text-blue-400" />
                      <div>
                        <p className="text-xs text-gray-500">LinkedIn</p>
                        <p className="text-blue-400 text-sm hover:underline">View Profile</p>
                      </div>
                    </a>
                  )}
                  {selectedMember.instagram_url && (
                    <a href={selectedMember.instagram_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-black/20 hover:bg-black/30">
                      <Instagram size={18} className="text-pink-400" />
                      <div>
                        <p className="text-xs text-gray-500">Instagram</p>
                        <p className="text-pink-400 text-sm hover:underline">View Profile</p>
                      </div>
                    </a>
                  )}
                </div>
              </div>

              {/* Profile Information */}
              <div>
                <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Profile Information</h3>
                <div className="space-y-3">
                  {selectedMember.work_description && (
                    <div className="p-4 rounded-xl bg-black/20">
                      <h4 className="text-sm font-semibold text-brand-gold mb-2">Work Description</h4>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">{selectedMember.work_description}</p>
                    </div>
                  )}
                  {selectedMember.professional_insights && (
                    <div className="p-4 rounded-xl bg-black/20">
                      <h4 className="text-sm font-semibold text-brand-gold mb-2">Professional Insights</h4>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">{selectedMember.professional_insights}</p>
                    </div>
                  )}
                  {selectedMember.personal_insights && (
                    <div className="p-4 rounded-xl bg-black/20">
                      <h4 className="text-sm font-semibold text-brand-gold mb-2">Personal Insights</h4>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">{selectedMember.personal_insights}</p>
                    </div>
                  )}
                  {selectedMember.interests && (
                    <div className="p-4 rounded-xl bg-black/20">
                      <h4 className="text-sm font-semibold text-brand-gold mb-2">Interests</h4>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">{selectedMember.interests}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Membership Details */}
              <div>
                <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Membership Details</h3>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="p-3 rounded-xl bg-black/20">
                    <p className="text-xs text-gray-500">Member Since</p>
                    <p className="text-gray-300 text-sm">{formatDate(selectedMember.created_at)}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-black/20">
                    <p className="text-xs text-gray-500">Payment Status</p>
                    <p className="text-gray-300 text-sm">{selectedMember.payment_status || 'N/A'}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-black/20">
                    <p className="text-xs text-gray-500">User ID</p>
                    <p className="text-gray-400 text-xs font-mono truncate">{selectedMember.user_id}</p>
                  </div>
                </div>
              </div>

              {/* Original Application Data */}
              {memberApplication && (
                <div>
                  <h3 className="text-xs font-bold text-brand-gold uppercase tracking-wider mb-3">Original Application Data</h3>
                  <div className="space-y-3">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="p-3 rounded-xl bg-black/20">
                        <p className="text-xs text-gray-500">Profession</p>
                        <p className="text-gray-300 text-sm">{memberApplication.profession || 'N/A'}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-black/20">
                        <p className="text-xs text-gray-500">Industry</p>
                        <p className="text-gray-300 text-sm">{memberApplication.industry || 'N/A'}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-black/20">
                        <p className="text-xs text-gray-500">Years of Experience</p>
                        <p className="text-gray-300 text-sm">{memberApplication.years_of_experience || 'N/A'}</p>
                      </div>
                      <div className="p-3 rounded-xl bg-black/20">
                        <p className="text-xs text-gray-500">How They Heard About Us</p>
                        <p className="text-gray-300 text-sm">{memberApplication.how_heard || 'N/A'}</p>
                      </div>
                    </div>
                    {memberApplication.education_credentials && (
                      <div className="p-4 rounded-xl bg-black/20">
                        <h4 className="text-sm font-semibold text-brand-gold mb-2">Education & Credentials</h4>
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{memberApplication.education_credentials}</p>
                      </div>
                    )}
                    {memberApplication.areas_of_interest && memberApplication.areas_of_interest.length > 0 && (
                      <div className="p-4 rounded-xl bg-black/20">
                        <h4 className="text-sm font-semibold text-brand-gold mb-2">Areas of Interest</h4>
                        <div className="flex flex-wrap gap-2">
                          {memberApplication.areas_of_interest.map((interest, idx) => (
                            <span key={idx} className="px-3 py-1 rounded-lg text-xs font-semibold bg-brand-gold/10 text-brand-gold border border-brand-gold/20">{interest}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="p-4 rounded-xl bg-black/20">
                      <h4 className="text-sm font-semibold text-brand-gold mb-2">Why They Wanted to Join</h4>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">{memberApplication.why_join || 'N/A'}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-black/20">
                      <h4 className="text-sm font-semibold text-brand-gold mb-2">Value They Can Bring</h4>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">{memberApplication.contribution || 'N/A'}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-black/20">
                      <h4 className="text-sm font-semibold text-brand-gold mb-2">Networking Goals</h4>
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">{memberApplication.networking_goals || 'N/A'}</p>
                    </div>
                    {memberApplication.notable_achievements && (
                      <div className="p-4 rounded-xl bg-black/20">
                        <h4 className="text-sm font-semibold text-brand-gold mb-2">Notable Achievements</h4>
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{memberApplication.notable_achievements}</p>
                      </div>
                    )}
                    <div className="p-3 rounded-xl bg-black/20">
                      <p className="text-xs text-gray-500">Applied On</p>
                      <p className="text-gray-300 text-sm">{formatDate(memberApplication.applied_at)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-brand-gold/10">
                <button onClick={() => handleSuspend(selectedMember)} disabled={actionLoading === selectedMember.id} className={`flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${selectedMember.membership_status === 'suspended' ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30'}`}>{selectedMember.membership_status === 'suspended' ? <><UserCheck size={18} /> Reactivate</> : <><Ban size={18} /> Suspend</>}</button>
                <button onClick={() => handleMakeAdmin(selectedMember)} disabled={actionLoading === selectedMember.id} className={`flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${selectedMember.admin_role ? 'bg-brand-gold/20 text-brand-gold hover:bg-brand-gold/30 border border-brand-gold/30' : 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/30'}`}><Shield size={18} />{selectedMember.admin_role ? 'Remove Admin' : 'Make Admin'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowEventModal(false)}>
          <div className="bg-brand-dark-light rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-brand-gold/20">
              <h3 className="text-xl font-bold gradient-text">{editingItem ? 'Edit Event' : 'Create Event'}</h3>
              <button onClick={() => setShowEventModal(false)} className="p-2 rounded-lg hover:bg-white/10"><X size={24} className="text-gray-400" /></button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Title <span className="text-brand-gold">*</span></label>
                  <input type="text" value={eventForm.title || ''} onChange={e => setEventForm({...eventForm, title: e.target.value})} placeholder="Event title" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Category</label>
                  <select value={eventForm.category || ''} onChange={e => setEventForm({...eventForm, category: e.target.value})} className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white focus:border-brand-gold focus:outline-none transition-colors cursor-pointer">
                    <option value="">Select category...</option>
                    {EVENT_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                <textarea value={eventForm.description || ''} onChange={e => setEventForm({...eventForm, description: e.target.value})} placeholder="Full event description" rows={3} className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Preview Description (for guests)</label>
                <textarea value={eventForm.preview_description || ''} onChange={e => setEventForm({...eventForm, preview_description: e.target.value})} placeholder="Short description for guests" rows={2} className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors resize-none" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Date <span className="text-brand-gold">*</span></label>
                  <input type="date" value={eventForm.event_date || ''} onChange={e => setEventForm({...eventForm, event_date: e.target.value})} className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Time <span className="text-brand-gold">*</span></label>
                  <input type="text" value={eventForm.event_time || ''} onChange={e => setEventForm({...eventForm, event_time: e.target.value})} placeholder="e.g., 6:00 PM" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Venue</label>
                <input type="text" value={eventForm.venue || ''} onChange={e => setEventForm({...eventForm, venue: e.target.value})} placeholder="Event location" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
              </div>
              
              {/* Image Upload for Events */}
              <ImageUploader 
                currentUrl={eventForm.image_url} 
                onUpload={handleEventImageUpload} 
                uploading={uploadingImage}
                label="Event Image"
              />
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Max Attendees</label>
                  <input type="number" value={eventForm.max_attendees || ''} onChange={e => setEventForm({...eventForm, max_attendees: parseInt(e.target.value) || 0})} placeholder="50" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Published</label>
                  <label className="flex items-center gap-3 cursor-pointer mt-2">
                    <input type="checkbox" checked={eventForm.is_published || false} onChange={e => setEventForm({...eventForm, is_published: e.target.checked})} className="w-5 h-5 rounded border-brand-gold/30 bg-black/30 text-brand-gold focus:ring-brand-gold" />
                    <span className="text-white">Make event visible</span>
                  </label>
                </div>
              </div>
              
              {/* Eventbrite Integration */}
              {!editingItem && (
                <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={eventForm.publish_to_eventbrite || false} 
                      onChange={e => setEventForm({...eventForm, publish_to_eventbrite: e.target.checked})} 
                      className="w-5 h-5 rounded border-orange-500/30 bg-black/30 text-orange-500 focus:ring-orange-500" 
                    />
                    <div>
                      <span className="text-white font-medium">Publish to Eventbrite</span>
                      <p className="text-gray-400 text-xs mt-1">Cross-publish this event to Eventbrite for additional visibility</p>
                    </div>
                  </label>
                </div>
              )}
              {eventForm.eventbrite_url && (
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <p className="text-green-400 text-sm font-medium mb-1">Published on Eventbrite</p>
                  <a href={eventForm.eventbrite_url} target="_blank" rel="noopener noreferrer" className="text-brand-gold hover:underline text-sm break-all">{eventForm.eventbrite_url}</a>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-brand-gold/20">
              <button onClick={() => setShowEventModal(false)} className="px-6 py-2.5 rounded-xl bg-white/5 text-gray-400 font-semibold hover:bg-white/10">Cancel</button>
              <button onClick={saveEvent} disabled={actionLoading === 'event' || uploadingImage} className="btn-primary px-6 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50"><Save size={18} />{actionLoading === 'event' ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Dinner Event Modal */}
      {showDinnerModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDinnerModal(false)}>
          <div className="bg-brand-dark-light rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-brand-gold/20">
              <h3 className="text-xl font-bold gradient-text">{editingItem ? 'Edit Dinner Event' : 'Create Dinner Event'}</h3>
              <button onClick={() => setShowDinnerModal(false)} className="p-2 rounded-lg hover:bg-white/10"><X size={24} className="text-gray-400" /></button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Title <span className="text-brand-gold">*</span></label>
                <input type="text" value={dinnerForm.title || ''} onChange={e => setDinnerForm({...dinnerForm, title: e.target.value})} placeholder="Dinner event title" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Category</label>
                <select value={dinnerForm.category || ''} onChange={e => setDinnerForm({...dinnerForm, category: e.target.value})} className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white focus:border-brand-gold focus:outline-none transition-colors cursor-pointer">
                  {DINNER_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Description</label>
                <textarea value={dinnerForm.description || ''} onChange={e => setDinnerForm({...dinnerForm, description: e.target.value})} placeholder="Event description" rows={3} className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors resize-none" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Date <span className="text-brand-gold">*</span></label>
                  <input type="date" value={dinnerForm.event_date || ''} onChange={e => setDinnerForm({...dinnerForm, event_date: e.target.value})} className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Time <span className="text-brand-gold">*</span></label>
                  <input type="text" value={dinnerForm.event_time || ''} onChange={e => setDinnerForm({...dinnerForm, event_time: e.target.value})} placeholder="e.g., 7:00 PM" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Venue</label>
                <input type="text" value={dinnerForm.venue || ''} onChange={e => setDinnerForm({...dinnerForm, venue: e.target.value})} placeholder="Restaurant name & address" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
              </div>
              
              {/* Image Upload for Dinner Events */}
              <ImageUploader 
                currentUrl={dinnerForm.image_url} 
                onUpload={handleDinnerImageUpload} 
                uploading={uploadingImage}
                label="Dinner Event Image"
              />
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Price ($) <span className="text-brand-gold">*</span></label>
                  <input type="number" value={dinnerForm.price || ''} onChange={e => setDinnerForm({...dinnerForm, price: parseFloat(e.target.value) || 0})} placeholder="150" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Max Attendees</label>
                  <input type="number" value={dinnerForm.max_attendees || ''} onChange={e => setDinnerForm({...dinnerForm, max_attendees: parseInt(e.target.value) || 0})} placeholder="20" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-brand-gold/20">
              <button onClick={() => setShowDinnerModal(false)} className="px-6 py-2.5 rounded-xl bg-white/5 text-gray-400 font-semibold hover:bg-white/10">Cancel</button>
              <button onClick={saveDinnerEvent} disabled={actionLoading === 'dinner' || uploadingImage} className="btn-primary px-6 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50"><Save size={18} />{actionLoading === 'dinner' ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Book Modal */}
      {showBookModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowBookModal(false)}>
          <div className="bg-brand-dark-light rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-brand-gold/20">
              <h3 className="text-xl font-bold gradient-text">{editingItem ? 'Edit Book' : 'Add Book'}</h3>
              <button onClick={() => setShowBookModal(false)} className="p-2 rounded-lg hover:bg-white/10"><X size={24} className="text-gray-400" /></button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Title <span className="text-brand-gold">*</span></label>
                <input type="text" value={bookForm.title || ''} onChange={e => setBookForm({...bookForm, title: e.target.value})} placeholder="Book title" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Author <span className="text-brand-gold">*</span></label>
                <input type="text" value={bookForm.author || ''} onChange={e => setBookForm({...bookForm, author: e.target.value})} placeholder="Author name" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
              </div>
              
              {/* Image Upload for Books */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Book Cover</label>
                <div className="flex gap-4 items-start">
                  <div className="relative">
                    {bookForm.photo_url ? (
                      <img src={bookForm.photo_url} alt="Book Cover" className="w-24 h-32 rounded-xl object-cover border-2 border-brand-gold/30" />
                    ) : (
                      <div className="w-24 h-32 rounded-xl bg-black/30 border-2 border-dashed border-brand-gold/30 flex items-center justify-center">
                        <BookOpen size={28} className="text-gray-500" />
                      </div>
                    )}
                    <label className="absolute -bottom-2 -right-2 w-10 h-10 bg-brand-gold rounded-xl flex items-center justify-center cursor-pointer hover:bg-yellow-400 transition-colors">
                      <Camera size={18} className="text-black" />
                      <input type="file" accept="image/*" onChange={handleBookImageUpload} className="hidden" disabled={uploadingImage} />
                    </label>
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-500 text-xs mb-2">Click the camera icon to upload a cover image, or enter a URL below:</p>
                    <input 
                      type="text" 
                      value={bookForm.photo_url || ''} 
                      onChange={e => setBookForm({...bookForm, photo_url: e.target.value})}
                      placeholder="https://... (optional)"
                      className="w-full px-4 py-2 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors text-sm"
                    />
                    {uploadingImage && <p className="text-brand-gold text-xs mt-2">Uploading image...</p>}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Chapters per Week</label>
                <input type="number" value={bookForm.chapters_per_week || ''} onChange={e => setBookForm({...bookForm, chapters_per_week: parseInt(e.target.value) || 0})} placeholder="2" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={bookForm.is_current || false} onChange={e => setBookForm({...bookForm, is_current: e.target.checked})} className="w-5 h-5 rounded border-brand-gold/30 bg-black/30 text-brand-gold focus:ring-brand-gold" />
                  <span className="text-white">Set as currently reading book</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-brand-gold/20">
              <button onClick={() => setShowBookModal(false)} className="px-6 py-2.5 rounded-xl bg-white/5 text-gray-400 font-semibold hover:bg-white/10">Cancel</button>
              <button onClick={saveBook} disabled={actionLoading === 'book' || uploadingImage} className="btn-primary px-6 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50"><Save size={18} />{actionLoading === 'book' ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Venue Modal */}
      {showVenueModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowVenueModal(false)}>
          <div className="bg-brand-dark-light rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-brand-gold/20">
              <h3 className="text-xl font-bold gradient-text">{editingItem ? 'Edit Venue' : 'Add Venue'}</h3>
              <button onClick={() => setShowVenueModal(false)} className="p-2 rounded-lg hover:bg-white/10"><X size={24} className="text-gray-400" /></button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Venue Name <span className="text-brand-gold">*</span></label>
                <input type="text" value={venueForm.venue_name || ''} onChange={e => setVenueForm({...venueForm, venue_name: e.target.value})} placeholder="e.g., Starbucks Downtown" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Address</label>
                <input type="text" value={venueForm.address || ''} onChange={e => setVenueForm({...venueForm, address: e.target.value})} placeholder="Full address" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Meeting Date <span className="text-brand-gold">*</span></label>
                  <input type="date" value={venueForm.meeting_date || ''} onChange={e => setVenueForm({...venueForm, meeting_date: e.target.value})} className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Meeting Time</label>
                  <input type="text" value={venueForm.meeting_time || ''} onChange={e => setVenueForm({...venueForm, meeting_time: e.target.value})} placeholder="e.g., 10:00 AM" className="w-full px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-white placeholder-gray-500 focus:border-brand-gold focus:outline-none transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Venue Image</label>
                <div className="space-y-3">
                  {venueForm.image_url && (
                    <div className="relative w-full h-32 rounded-xl overflow-hidden bg-black/30">
                      <img src={venueForm.image_url} alt="Venue" className="w-full h-full object-contain" />
                      <button onClick={() => setVenueForm({...venueForm, image_url: ''})} className="absolute top-2 right-2 p-1 bg-red-500/80 rounded-full hover:bg-red-500"><X size={16} className="text-white" /></button>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-black/30 border border-brand-gold/20 rounded-xl text-gray-400 cursor-pointer hover:border-brand-gold transition-colors">
                      <Upload size={18} />
                      <span>{uploadingImage ? 'Uploading...' : 'Upload Image'}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setUploadingImage(true);
                          try {
                            const url = await uploadImage(file, 'venues');
                            if (url) setVenueForm({...venueForm, image_url: url});
                          } catch (error) {
                            console.error('Upload error:', error);
                            alert('Failed to upload image');
                          }
                          setUploadingImage(false);
                        }
                      }} disabled={uploadingImage} />
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={venueForm.is_next || false} onChange={e => setVenueForm({...venueForm, is_next: e.target.checked})} className="w-5 h-5 rounded border-brand-gold/30 bg-black/30 text-brand-gold focus:ring-brand-gold" />
                  <span className="text-white">Set as next upcoming meeting</span>
                </label>
              </div>
              
              {/* Eventbrite Integration */}
              {!editingItem && (
                <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={venueForm.publish_to_eventbrite || false} 
                      onChange={e => setVenueForm({...venueForm, publish_to_eventbrite: e.target.checked})} 
                      className="w-5 h-5 rounded border-orange-500/30 bg-black/30 text-orange-500 focus:ring-orange-500" 
                    />
                    <div>
                      <span className="text-white font-medium">Publish to Eventbrite</span>
                      <p className="text-gray-400 text-xs mt-1">Cross-publish this Book Club meeting to Eventbrite for additional visibility</p>
                    </div>
                  </label>
                </div>
              )}
              {venueForm.eventbrite_url && (
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <p className="text-green-400 text-sm font-medium mb-1">Published on Eventbrite</p>
                  <a href={venueForm.eventbrite_url} target="_blank" rel="noopener noreferrer" className="text-brand-gold hover:underline text-sm break-all">{venueForm.eventbrite_url}</a>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-brand-gold/20">
              <button onClick={() => setShowVenueModal(false)} className="px-6 py-2.5 rounded-xl bg-white/5 text-gray-400 font-semibold hover:bg-white/10">Cancel</button>
              <button onClick={saveVenue} disabled={actionLoading === 'venue'} className="btn-primary px-6 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50"><Save size={18} />{actionLoading === 'venue' ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
          <div className="bg-brand-dark border border-brand-gold/20 rounded-2xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-xl font-bold text-white mb-3">{confirmModal.title}</h3>
              <p className="text-gray-400">{confirmModal.message}</p>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-brand-gold/20">
              <button 
                onClick={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })} 
                className="px-6 py-2.5 rounded-xl bg-white/5 text-gray-400 font-semibold hover:bg-white/10"
              >
                Cancel
              </button>
              <button 
                onClick={confirmModal.onConfirm} 
                className="px-6 py-2.5 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
